// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import ClassStep from "./class-step.js"
import SchoolClassGroup from "./school-class-group.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "class_step_id": {
      "column": {
        "default": null,
        "name": "class_step_id",
        "null": false,
        "type": null
      },
      "name": "class_step_id",
      "selected_by_default": null,
      "translated": null
    },
    "school_class_group_id": {
      "column": {
        "default": null,
        "name": "school_class_group_id",
        "null": false,
        "type": null
      },
      "name": "school_class_group_id",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "SchoolClassGroupClassStep",
  "collectionKey": "school_class_group_class_steps",
  "collectionName": "school_class_group_class_steps",
  "i18nKey": "school_class_group_class_step",
  "camelizedLower": "schoolClassGroupClassStep",
  "name": "SchoolClassGroupClassStep",
  "nameDasherized": "school-class-group-class-step",
  "pluralName": "school_class_group_class_steps",
  "ransackable_associations": [
    {
      "className": "SchoolClassGroupClassStep::Audit",
      "collectionName": "school_class_group_class_step_audits",
      "foreignKey": "school_class_group_class_step_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "SchoolClassGroupClassStepAudit",
      "through": null
    },
    {
      "className": "ClassStep",
      "collectionName": "class_steps",
      "foreignKey": "class_step_id",
      "name": "class_step",
      "macro": "belongs_to",
      "resource_name": "ClassStep",
      "through": null
    },
    {
      "className": "SchoolClassGroupClassStep::Audit",
      "collectionName": "school_class_group_class_step_audits",
      "foreignKey": "school_class_group_class_step_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "SchoolClassGroupClassStepAudit",
      "through": null
    },
    {
      "className": "SchoolClassGroup",
      "collectionName": "school_class_groups",
      "foreignKey": "school_class_group_id",
      "name": "school_class_group",
      "macro": "belongs_to",
      "resource_name": "SchoolClassGroup",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "class_step_id",
      "column": {
        "default": null,
        "name": "class_step_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "school_class_group_id",
      "column": {
        "default": null,
        "name": "school_class_group_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "ClassStep",
      "collectionName": "class_steps",
      "foreignKey": "class_step_id",
      "name": "class_step",
      "macro": "belongs_to",
      "resource_name": "ClassStep",
      "through": null
    },
    {
      "className": "SchoolClassGroup",
      "collectionName": "school_class_groups",
      "foreignKey": "school_class_group_id",
      "name": "school_class_group",
      "macro": "belongs_to",
      "resource_name": "SchoolClassGroup",
      "through": null
    }
  ],
  "paramKey": "school_class_group_class_step",
  "primaryKey": "id"
}

/** Frontend model for SchoolClassGroupClassStep. */
export default class SchoolClassGroupClassStep extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {any} */
  classStepId() {
    return this.readAttributeUnderscore("class_step_id")
  }

  /** @returns {boolean} */
  hasClassStepId() {
    const value = this.classStepId()

    return this._isPresent(value)
  }

  /** @returns {any} */
  schoolClassGroupId() {
    return this.readAttributeUnderscore("school_class_group_id")
  }

  /** @returns {boolean} */
  hasSchoolClassGroupId() {
    const value = this.schoolClassGroupId()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("./class-step.js").default | null} */
  classStep() {
    return this._readBelongsToReflection({modelClass: ClassStep, reflectionName: "class_step"})
  }

  /** @returns {Promise<import("./class-step.js").default | null>} */
  loadClassStep() {
    if (!("classStepId" in this)) throw new Error("Foreign key method wasn't defined: classStepId")

    const id = this.classStepId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "class_step",
        model: this,
        modelClass: ClassStep
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./class-step.js").default | null>} */
  ensureClassStepLoaded() {
    return this.ensureAssociationLoaded("class_step")
  }

  /** @returns {import("./school-class-group.js").default | null} */
  schoolClassGroup() {
    return this._readBelongsToReflection({modelClass: SchoolClassGroup, reflectionName: "school_class_group"})
  }

  /** @returns {Promise<import("./school-class-group.js").default | null>} */
  loadSchoolClassGroup() {
    if (!("schoolClassGroupId" in this)) throw new Error("Foreign key method wasn't defined: schoolClassGroupId")

    const id = this.schoolClassGroupId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "school_class_group",
        model: this,
        modelClass: SchoolClassGroup
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./school-class-group.js").default | null>} */
  ensureSchoolClassGroupLoaded() {
    return this.ensureAssociationLoaded("school_class_group")
  }
}
